import { Box, Typography, keyframes, useTheme } from '@mui/material';
import { memo } from 'react';
import AIEnergyIcon from "../styled/AIEnergyIcon";

// Define keyframes outside component for better performance
const energyPulse = keyframes`
    0%, 100% {
        opacity: 0.4;
        transform: scale(1);
    }
    50% {
        opacity: 0.8;
        transform: scale(1.2);
    }
`;

const ringPulse = keyframes`
    0% {
        transform: scale(0.9);
        opacity: 0.7;
    }
    70%, 100% {
        transform: scale(1.5);
        opacity: 0;
    }
`;

const textPulse = keyframes`
    0%, 100% {
        opacity: 0.7;
    }
    50% {
        opacity: 1;
    }
`;

const FallbackUILoader = ({ message = 'Loading...' }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    // Get colors from theme with fallbacks
    const primaryColor = theme.palette.primary?.main || '#1E63F6';
    const infoColor = theme.palette.info?.main || '#06b6d4';
    const successColor = theme.palette.success?.main || '#10b981';
    const backgroundColor = theme.palette.background?.default || '#ffffff';
    const textSecondaryColor = theme.palette.text?.secondary || 'rgba(0, 0, 0, 0.6)';
    
    // Create dynamic keyframes based on theme
    const dynamicIconFloat = keyframes`
        0%, 100% {
            transform: translateY(0) scale(1);
            filter: brightness(1) drop-shadow(0 0 6px ${isDarkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(30, 99, 246, 0.3)'});
        }
        50% {
            transform: translateY(-4px) scale(1.05);
            filter: brightness(1.2) drop-shadow(0 0 10px ${isDarkMode ? 'rgba(96, 165, 250, 0.5)' : 'rgba(30, 99, 246, 0.5)'});
        }
    `;
    
    // Dynamic colors based on theme mode
    const colors = {
        ringBorder: isDarkMode ? `${primaryColor}40` : `${primaryColor}40`,
        orb1: isDarkMode ? '#22D3EE' : infoColor,
        orb2: isDarkMode ? '#60A5FA' : primaryColor,
        orb3: isDarkMode ? '#93C5FD' : '#3B82F6',
        backgroundColor: isDarkMode 
            ? `rgba(15, 23, 42, 0.9)` 
            : 'rgba(255, 255, 255, 0.9)',
        textColor: isDarkMode ? 'rgba(156, 163, 175, 0.9)' : textSecondaryColor,
    };
    
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(3px)',
                backgroundColor: colors.backgroundColor,
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 9999,
                overflow: 'hidden'
            }}
        >
            {/* Simplified Energy Loader */}
            <Box
                sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Pulse Ring */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        border: `2px solid ${colors.ringBorder}`,
                        animation: `${ringPulse} 2s ease-out infinite`,
                    }}
                />
                
                {/* Main Icon */}
                <Box
                    sx={{
                        animation: `${dynamicIconFloat} 1.5s ease-in-out infinite`,
                        zIndex: 2,
                    }}
                >
                    <AIEnergyIcon 
                        size="large"
                        animate={true}
                        isRotating={true}
                        color={isDarkMode ? '#22D3EE' : primaryColor}
                    />
                </Box>
                
                {/* Small Energy Orbs */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        animation: 'spin 3s linear infinite',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        }
                    }}
                >
                    {[0, 1, 2].map((index) => (
                        <Box
                            key={index}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                width: 8,
                                height: 8,
                                marginTop: -4,
                                marginLeft: -4,
                                borderRadius: '50%',
                                backgroundColor: index === 0 ? colors.orb1 : 
                                              index === 1 ? colors.orb2 : colors.orb3,
                                transform: `rotate(${index * 120}deg) translateX(28px)`,
                                opacity: isDarkMode ? 0.9 : 0.8,
                                boxShadow: isDarkMode 
                                    ? `0 0 6px ${index === 0 ? colors.orb1 : index === 1 ? colors.orb2 : colors.orb3}80`
                                    : `0 0 6px ${index === 0 ? colors.orb1 : index === 1 ? colors.orb2 : colors.orb3}40`,
                            }}
                        />
                    ))}
                </Box>
            </Box>
            
            {/* Loading Message */}
            <Typography
                variant="body1"
                sx={{
                    mt: 3,
                    color: colors.textColor,
                    fontWeight: 'medium',
                    animation: `${textPulse} 2s ease-in-out infinite`,
                }}
            >
                {message}
            </Typography>
        </Box>
    );
};

export default memo(FallbackUILoader);